import { MultiSelectFilter } from './MultiSelectFilter';
import { cascadeOptions, isFilterSelected, newDisabler } from '@utils/filters';
import { useMemo } from 'react';
import { differenceObjects } from '@utils/data';
import { useNameMapQuery, useOrgMapQuery } from '@features/redux/apis/org';
import { Box } from '@mui/material';

export type OrgFilterInput = {
    companies: { [key: string]: boolean };
    resellers: { [key: string]: boolean };
    locations: { [key: string]: boolean };
};

export enum OrgFilterOption {
    Company = 'companies',
    Reseller = 'resellers',
    Location = 'locations',
}

interface OrgFiltersProps {
    handleClear: () => void;
    setOrgFilters: (option: string, value: string, selectOne?: boolean) => void;
    orgFilters: OrgFilterInput;
    showLocations?: boolean;
    showResellers?: boolean;
    disabled?: boolean;
}

const shouldDisable = newDisabler([OrgFilterOption.Company, OrgFilterOption.Reseller, OrgFilterOption.Location]);

// -----------------------------------------------------------------------------
// Helper to detect if the special "All" ("*") option is selected
// -----------------------------------------------------------------------------
const hasNoneSelected = (selected: { [key: string]: boolean }): boolean => {
    // If no keys are marked true, interpret as all selected.
    return !Object.values(selected).some(Boolean);
};

export const OrgFilters = ({
    handleClear,
    setOrgFilters,
    orgFilters,
    showResellers = true,
    showLocations = true,
    disabled = false,
}: OrgFiltersProps) => {
    const { data: { companyLocationMap = {}, companyResellerMap = {}, resellerLocationMap = {} } = {} } =
        useOrgMapQuery({});
    const { data: { companies = {}, resellers = {}, locations = {} } = {} } = useNameMapQuery({});

    const companyValues = useMemo(
        () => Object.entries(companies).map(([key, value]) => ({ companyId: key, companyName: value })),
        [companies]
    );

    /** This could have less iteration/traversal.  */
    const resellerValues = useMemo(() => {
        const options = Object.entries(differenceObjects(companies, resellers)).map(([key, value]) => ({
            resellerId: key,
            resellerName: value,
        }));

        return cascadeOptions({
            options,
            parent: orgFilters.companies,
            map: companyResellerMap,
            idKey: 'resellerId',
        });
    }, [resellers, orgFilters.companies, companyResellerMap]);

    const locationValues = useMemo(() => {
        const allResellers = hasNoneSelected(orgFilters.resellers);
        const allCompanies = hasNoneSelected(orgFilters.companies);

        const options = Object.entries(locations).map(([key, value]) => ({
            locationId: key,
            locationName: `#${key} - ${value}`,
        }));

        // 1. All resellers AND all companies selected → show all locations.
        if (allResellers && allCompanies) {
            return options;
        }

        // 2. All resellers BUT NOT all companies → gather locations for the
        //    chosen companies and their respective resellers.
        if (allResellers && !allCompanies) {
            const selectedCompanies = Object.keys(orgFilters.companies).filter((id) => orgFilters.companies[id]);
            const companyResellers = selectedCompanies.flatMap((companyId) => companyResellerMap[companyId] || []);
            const resellerLocations = companyResellers.flatMap((resellerId) => resellerLocationMap[resellerId] || []);
            const companyLocations = selectedCompanies.flatMap((companyId) => companyLocationMap[companyId] || []);

            const targetIds = new Set<string>([...resellerLocations.map(String), ...companyLocations.map(String)]);
            return options.filter(({ locationId }) => targetIds.has(locationId));
        }

        // 3. Default cascade behaviour (mirrors previous implementation).
        const byReseller = isFilterSelected(orgFilters, OrgFilterOption.Reseller);
        return cascadeOptions({
            options,
            parent: byReseller ? orgFilters.resellers : orgFilters.companies,
            map: byReseller ? resellerLocationMap : companyLocationMap,
            idKey: 'locationId',
        });
    }, [
        locations,
        orgFilters.resellers,
        orgFilters.companies,
        companyResellerMap,
        resellerLocationMap,
        companyLocationMap,
    ]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
            }}>
            <MultiSelectFilter
                search
                disabled={disabled || shouldDisable(OrgFilterOption.Company, orgFilters)}
                disabledWithClear={shouldDisable(OrgFilterOption.Company, orgFilters)}
                onClear={handleClear}
                idKey={'companyId'}
                label={'Company'}
                options={companyValues}
                value={orgFilters.companies}
                onChange={(value: { companyId: string; companyName: string }) =>
                    setOrgFilters(OrgFilterOption.Company, value.companyId)
                }
                getLabelValue={(option) => option.companyName}
            />

            {showResellers && (
                <MultiSelectFilter
                    search
                    disabled={disabled || shouldDisable(OrgFilterOption.Reseller, orgFilters)}
                    disabledWithClear={shouldDisable(OrgFilterOption.Reseller, orgFilters)}
                    onClear={handleClear}
                    idKey={'resellerId'}
                    label={'Reseller'}
                    options={resellerValues}
                    value={orgFilters.resellers}
                    onChange={(value: { resellerId: string; resellerName: string }) =>
                        setOrgFilters(OrgFilterOption.Reseller, value.resellerId)
                    }
                    getLabelValue={(option) => option.resellerName}
                />
            )}

            {showLocations && (
                <MultiSelectFilter
                    search
                    disabled={disabled || shouldDisable(OrgFilterOption.Location, orgFilters)}
                    disabledWithClear={shouldDisable(OrgFilterOption.Location, orgFilters)}
                    onClear={handleClear}
                    idKey={'locationId'}
                    label={'Location'}
                    options={locationValues}
                    value={orgFilters.locations}
                    onChange={(value: { locationId: string; locationName: string }) =>
                        setOrgFilters(OrgFilterOption.Location, value.locationId)
                    }
                    getLabelValue={(option) => option.locationName}
                />
            )}
        </Box>
    );
};
